import { format, parseISO } from "date-fns";
import {
  User,
  IssueActivity,
  IssueActivityType,
  MonitorActivity,
  MonitorActivityType,
} from "../../fixtures";
import { identity } from "lodash";

export function formatStatus(status: string) {
  return status;
}

export function formatDateTime(isoDateString: string) {
  try {
    return format(parseISO(isoDateString), "MMMM d, yyyy h:mm a");
  } catch (e) {
    console.error(e);
    return isoDateString;
  }
}

export function formatDate(isoDateString: string) {
  try {
    return format(parseISO(isoDateString), "MMMM d, yyyy");
  } catch (e) {
    console.error(e);
    return isoDateString;
  }
}

export function formatUser(user: User | null | undefined) {
  return user?.email;
}

export function formatIssueActivity(
  activity: IssueActivity,
  allUsers: User[] = [],
) {
  const formatUserById = (userId: string) => {
    const user = allUsers.find((u) => u.id === userId);
    return formatUser(user);
  };

  switch (activity.type) {
    case IssueActivityType.Assigned:
      return !activity.assignedUserId
        ? `${formatUserById(activity.assigningUserId)} unassigned issue`
        : activity.assigningUserId === activity.assignedUserId
          ? `${formatUserById(activity.assigningUserId)} self-assigned issue`
          : `${formatUserById(activity.assigningUserId)} assigned issue to ${formatUserById(activity.assignedUserId)}`;
    case IssueActivityType.Commented:
      return `${formatUserById(activity.commentingUserId)} commented: ${activity.commentBody}`;
    case IssueActivityType.Created:
      return "Issue reported by TrueBiz";
    case IssueActivityType.Resolved:
      return `Issue resolved by ${formatUserById(activity.resolvingUserId)}. ${activity.resolutionType}: ${activity.resolutionDescription}`;
    case IssueActivityType.Muted:
      return `Issue muted by ${formatUserById(activity.mutingUserId)}`;
  }
}

export function partitionBy<T>(
  arr: T[],
  pred: (t: T) => any = identity,
): T[][] {
  // like https://clojuredocs.org/clojure.core/partition-by

  if (!arr.length) {
    return [];
  }

  const result: T[][] = [];

  let lastTestValue = pred(arr[0]);
  let buff: T[] = [];

  for (const a of arr) {
    const testValue = pred(a);

    if (testValue === lastTestValue) {
      buff.push(a);
      continue;
    }

    result.push(buff);
    lastTestValue = testValue;
    buff = [a];
  }

  if (buff.length) {
    result.push(buff);
  }

  return result;
}

export function formatMonitorActivity(activity: MonitorActivity, count = 1) {
  if (activity.type === MonitorActivityType.HomepageScan) {
    const issuesLength = activity.detectedIssueIds.length;

    if (issuesLength === 0) {
      return `Home page scan${count === 1 ? "" : "s"}: no issues detected`;
    }

    return `Home page scan${count === 1 ? "" : "s"}: ${issuesLength} issue${issuesLength === 1 ? "" : "s"} detected`;
  }

  if (activity.type === MonitorActivityType.FullScan) {
    const issuesLength = activity.detectedIssueIds.length;

    if (issuesLength === 0) {
      return `Full scan${count === 1 ? "" : "s"}: no issues detected`;
    }

    return `Full scan${count === 1 ? "" : "s"}: ${issuesLength} issue${issuesLength === 1 ? "" : "s"} detected`;
  }

  if (activity.type === MonitorActivityType.Enrolled) {
    return `Enrolled in monitoring`;
  }

  if (activity.type === MonitorActivityType.Unenrolled) {
    return `Unenrolled in monitoring`;
  }

  if (activity.type === MonitorActivityType.Edited) {
    return `Enrollment modified`;
  }
}
